import React, { useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { ChevronDown } from '@/components/svg'
import { Campaign } from '@/services/api/campaign'
import { InvestmentChat } from '@/services/api/chatbot'
import useInvestmentChatbot from '@/services/hooks/useInvestmentChatbot'
import { useSubscribeChat } from '@/services/hooks/useSubscribeChat'
import { report, resolveErrorMessage, scrollUtil } from '@/utils'
import ButtonBase from '../ButtonBase'
import Type from '../Type'
import Message from './Message'

interface Props {
  campaign: Campaign
  className?: string
}

const ChatBot: React.FC<Props> = ({ campaign, className }) => {
  const [isFollowing, setFollowing] = useState(true)
  const [messages, setMessages] = useState<InvestmentChat[]>([])
  const scrollPositionRef = useRef<number>(0)
  const scrollableContainer = useRef<HTMLDivElement>(null)
  const { isError, data } = useInvestmentChatbot(campaign.slug)

  const { error: subscribeError } = useSubscribeChat({
    chatCode: campaign.channelId,
    setInvestmentMessages: setMessages,
  })

  useEffect(() => {
    if (!data) return

    setMessages(data)
  }, [data])

  useEffect(() => {
    const element = scrollableContainer.current
    if (element && isFollowing) {
      scrollPositionRef.current = element.scrollHeight
      scrollUtil.scrollToBottomOfElement(element)
    }
  }, [messages, isFollowing])

  const onScroll = () => {
    const chatContainer = scrollableContainer.current

    if (!chatContainer) {
      return
    }

    if (
      chatContainer.scrollTop + chatContainer.clientHeight >=
      scrollPositionRef.current
    ) {
      setFollowing(true)
    } else {
      setFollowing(false)
    }
  }

  const stopFollowing = useCallback(() => {
    setFollowing(false)
  }, [setFollowing])

  const startFollowing = useCallback(() => {
    setFollowing(true)
  }, [setFollowing])

  if (isError) {
    report.error(resolveErrorMessage(isError as Error))
    return null
  }
  // report it but still show recent investments, i.e. continue rendering
  if (subscribeError) {
    report.error(resolveErrorMessage(subscribeError))
  }

  return (
    <div className={classNames('relative overflow-hidden', className)}>
      <Type
        as="h3"
        variant="title-xs"
        className="absolute top-0 z-10 w-full rounded-t-2xl border border-core-gray-200 bg-white p-4 font-bold text-core-gray-800 shadow-light"
      >
        Investment Updates
      </Type>
      <div
        className="scrollbar h-72 overflow-y-scroll rounded-2xl border border-core-gray-200 bg-white px-4 pb-2 pt-16"
        ref={scrollableContainer}
        onScroll={onScroll}
        onClick={stopFollowing}
        onTouchMove={stopFollowing}
        onMouseDown={stopFollowing}
      >
        {messages.map((message) => (
          <Message message={message} key={message.id} />
        ))}
      </div>

      <ButtonBase
        className={classNames(
          'z-10 mx-auto -mt-4 flex h-[38px] w-[38px] items-center justify-center rounded-full bg-gray-1 p-3 drop-shadow-xl transition-opacity delay-100',
          {
            'opacity-0': isFollowing,
          }
        )}
        onClick={startFollowing}
      >
        <ChevronDown className={'stroke-white text-white'} />
      </ButtonBase>
    </div>
  )
}

export default ChatBot
